import React from 'react';
import moment from 'moment';
import {
  AppErrorCode,
  AssignTempBadgeInput,
  useAssignTempBadgeMutation,
} from 'apollo/generated/client-operations';
import { useIsMounted } from 'utils';
import { getISODateTimeToFormattedDate } from 'utils/dates';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { AlertService } from 'components/alertService';
import { IdCardDuotoneIcon, IdCardIcon } from 'components/icons';
import { AlreadyExistingBadgeMessage } from 'components/alreadyExistingBadgeMessage';
import { TempBadge } from 'containers/jobsite/tabs/jobsiteTempBadges/types';
import {
  ReactivateBadgeState,
  ReassignBadgeState,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/types';
import { Alert } from '@odin-labs/components';
import { WorkerOptionElement } from 'containers/utils';
import { StaticTempBadgePreview } from 'containers/jobsite/tabs/jobsiteTempBadges/components';
import { AssignTempBadgeFormData } from './types';
import {
  getFormDefaultValues,
  getFormInputsHook,
  useLoadWorkerOptions,
} from './AssignTempBadgeModalContent.forms';
import { BadgeInfoItem } from './SearchWorkerInfoItem';

const instructions =
  'Assigning a Temporary Badge overrides onboarding, suspensions, and bans.';

export interface AssignTempBadgeModalContentProps {
  closeModal: () => void;
  onAction: (args: {
    tempBadge?: TempBadge;
    reassignState?: ReassignBadgeState;
  }) => void;
  jobsiteId: string;
  jobsiteName: string;
  tempBadge: TempBadge;
  reactivateBadge: (reactivateBadgeState: ReactivateBadgeState) => void;
}

export function AssignTempBadgeModalContent(
  props: AssignTempBadgeModalContentProps,
): React.ReactElement {
  const {
    jobsiteId,
    tempBadge,
    closeModal,
    onAction,
    reactivateBadge,
    jobsiteName,
  } = props;
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  const [assignTempBadge] = useAssignTempBadgeMutation();

  const onSubmit: FormOnSubmit<AssignTempBadgeFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    const { workerId, expirationDate } = data;

    const assignTemporaryBadge = async (
      options?: Pick<AssignTempBadgeInput, 'force'>,
    ): Promise<ReturnType<typeof assignTempBadge>> => {
      return assignTempBadge({
        variables: {
          input: {
            force: options?.force,
            id: tempBadge.id,
            workerId: workerId.value,
            startDate: moment().toDate(),
            endDate: expirationDate.endOf('day').toDate(),
            cardIssueReason: data.tempBadgeReason.trim(),
            workerCardIdsToDeactivate: data.badges
              ?.filter(({ selected }) => selected)
              .map(({ workerCard }) => workerCard.workerCardId),
          },
        },
      });
    };

    try {
      const { data: result } = await assignTemporaryBadge();
      const registeredTempBadge = result.assignTempBadge.tempBadge;
      onAction({ tempBadge: registeredTempBadge });
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { scope, workerCardInfo, canBeReactivated } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (scope === 'onDifferentWorker') {
          onAction({
            reassignState: {
              workerCardInfo,
              reassignBadge: async (): Promise<void> => {
                await assignTemporaryBadge({ force: true });
              },
            },
          });
        } else {
          const { workerCardId } = workerCardInfo?.[0] ?? {};
          formApi.setError('workerId', {
            message: (
              <AlreadyExistingBadgeMessage
                canBeReactivated={canBeReactivated}
                errorMessage={workerCardAlreadyExistsError.message}
                onClick={(): void =>
                  reactivateBadge({
                    workerCardIdToReactivate: workerCardId,
                    badgesToDeactivate: data.badges,
                    expirationDate: data.expirationDate,
                  })
                }
              />
            ),
            shouldFocus: true,
          });
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const [isBannedOnSelectedJobsite, setIsBannedOnSelectedJobsite] =
    React.useState(false);
  const [isSuspendedOnSelectedJobsite, setIsSuspendedOnSelectedJobsite] =
    React.useState(false);
  const [suspensionDate, setSuspensionDate] = React.useState<string | null>(
    null,
  );

  const { loadWorkerOptions } = useLoadWorkerOptions({ jobsiteId });
  const handleWorkerSelected = React.useCallback(
    (opt: WorkerOptionElement | null) => {
      setIsBannedOnSelectedJobsite(
        Boolean(opt?.isBanned && !opt?.banExpiresAt),
      );
      setIsSuspendedOnSelectedJobsite(
        Boolean(opt?.isBanned && opt?.banExpiresAt),
      );
      setSuspensionDate(opt?.banExpiresAt ?? null);
    },
    [],
  );

  const formInputs = React.useMemo(
    () =>
      getFormInputsHook({
        loadWorkerOptions,
        onWorkerSelected: handleWorkerSelected,
      }),
    [loadWorkerOptions, handleWorkerSelected],
  );
  const defaultValues = React.useMemo(() => getFormDefaultValues(), []);
  const badgeInfo = (
    <div className="odin-flex odin-flex-col odin-mb-6">
      <div className="odin-flex odin-justify-center odin-mb-4">
        <StaticTempBadgePreview
          templateName={tempBadge.badgeTemplate?.templateName}
          badgeName={tempBadge.name}
          jobsiteName={jobsiteName}
        />
      </div>
      <div className="odin-flex odin-gap-x-4 odin-mb-8">
        <BadgeInfoItem icon={IdCardIcon} text={tempBadge.name} />
        <BadgeInfoItem icon={IdCardDuotoneIcon} text={tempBadge.cardNumber} />
      </div>
      <p>{instructions}</p>
      {isBannedOnSelectedJobsite && (
        <Alert
          type="danger"
          text={`This worker is permanently banned from ${jobsiteName}. A temporary badge will grant immediate access.`}
        />
      )}
      {isSuspendedOnSelectedJobsite && (
        <Alert
          type="warn"
          text={`This worker is suspended from ${jobsiteName} until ${getISODateTimeToFormattedDate(
            suspensionDate,
          )}. A temporary badge will grant immediate access.`}
        />
      )}
    </div>
  );

  return (
    <ModalFormContent
      setOpen={closeModal}
      renderAbove={badgeInfo}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Assign"
      actionButtonWithSpinner={isFetching}
    />
  );
}
